import { AssignmentStatuses } from './types'
import { Assignment, AssignmentGroup, AssignmentResponse } from '../../models/assignments/Assignments'
import { Episode, EpisodeStates } from '../../models/episodes/Episodes'
import { EAPAndHPIProgramSupport } from '../../models/user/User'
import { ProgramCustomerPropertyId } from '../common/constants/customerProgram'
import { isCustomerProgramHPIExclusive } from '../healthPlan/utils'
import { Programs } from '../programConfigs/programConfigs'
import { getLTSessionTypeAndRate } from '../sessions/utils'

export const DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME = 'daCareOnboardingHpiPayment'
export const DA_CARE_CONSENT_CONTENT_NAME = 'daCareConsent'
export const CARE_ONBOARDING_HPI_PAYMENT_REVIEWED_RESPONSE_KEY = 'careOnboardingHpiPaymentReviewed'

export const DA_CARE_ONBOARDING_INTAKE_CONTENT_NAMES = [
  'daCouplesClientIntake',
  'daFamilyClientIntake',
  'daIndividualClientIntake',
] as const

export type ProgramCoverage = Record<string, EAPAndHPIProgramSupport> | undefined
export type CareOnboardingHpiPaymentEstimateState = 'loading' | 'show' | 'skip'

export type CareOnboardingHpiPaymentContext = {
  episodes: Episode[]
  estimateStateByEpisodeId: Record<string, CareOnboardingHpiPaymentEstimateState>
  hasFetchedPaymentMethods: boolean
  isZeroDollarCostShare: boolean
  programCoverage: ProgramCoverage
  reviewedEpisodeIds: Set<string>
}

export type CareOnboardingHpiCostEstimateRequest = {
  cacheKey: string
  providerId: string
  selectedProgram: ProgramCustomerPropertyId
  sessionType: string
}

/**
 * Builds the existing pre-booking cost-estimate request from the ANC episode.
 * A booked appointment is not required: the episode supplies the provider and program, and the
 * program plus the provider's completed-session count supplies the next billing session type.
 */
export const getCareOnboardingHpiCostEstimateRequest = ({
  dateOfBirth,
  episode,
  sessionNumber = 1,
}: {
  dateOfBirth?: string
  episode?: Episode
  sessionNumber?: number
}): CareOnboardingHpiCostEstimateRequest | undefined => {
  const programConfig = Programs.getProgramConfigByName(episode?.program_name)
  if (!episode?.provider_id || !programConfig?.sessionTypeConfig) return undefined

  const { sessionType } = getLTSessionTypeAndRate({
    dateOfBirth,
    programName: episode.program_name,
    sessionNumber,
    sessionsRates: {},
  })

  return {
    cacheKey: `care-onboarding_${episode.provider_id}_${episode.id}_${sessionType}`,
    providerId: episode.provider_id,
    selectedProgram: programConfig.customerPropertyId,
    sessionType,
  }
}

const isDaCareOnboardingIntake = (assignment: Assignment) =>
  DA_CARE_ONBOARDING_INTAKE_CONTENT_NAMES.includes(
    assignment.content.name as (typeof DA_CARE_ONBOARDING_INTAKE_CONTENT_NAMES)[number],
  )

const isHpiOnlyEpisode = (episode: Episode, programCoverage: ProgramCoverage) => {
  const customerPropertyId = Programs.getProgramConfigByName(episode.program_name)?.customerPropertyId
  const coverage = customerPropertyId ? programCoverage?.[customerPropertyId] : undefined

  return isCustomerProgramHPIExclusive(coverage)
}

export const isCareOnboardingHpiPaymentEpisodeEligible = ({
  episode,
  programCoverage,
}: {
  episode?: Episode
  programCoverage: ProgramCoverage
}) => Boolean(episode && episode.state === EpisodeStates.IN_PROGRESS && isHpiOnlyEpisode(episode, programCoverage))

/**
 * Returns eligible DA episode ids that should review HPI cost and payment information during Care
 * Onboarding. Appointment state and registration source do not gate this step.
 */
export const getCareOnboardingHpiPaymentEpisodeIds = ({
  assignments,
  episodes,
  programCoverage,
}: Pick<CareOnboardingHpiPaymentContext, 'episodes' | 'programCoverage'> & {
  assignments: Assignment[]
}) => {
  const daEpisodeIds = new Set(assignments.filter(isDaCareOnboardingIntake).map((assignment) => assignment.episode_id))

  return new Set(
    episodes
      .filter(
        (episode) =>
          daEpisodeIds.has(episode.id) && isCareOnboardingHpiPaymentEpisodeEligible({ episode, programCoverage }),
      )
      .map((episode) => episode.id),
  )
}

export const isCareOnboardingHpiPaymentAssignment = (assignment?: Assignment | null) =>
  assignment?.content.name === DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME

export const getCareOnboardingHpiPaymentConsentResponse = ({
  assignments,
  episodeId,
}: {
  assignments: Assignment[]
  episodeId?: string
}): AssignmentResponse | undefined =>
  assignments
    .filter(
      (assignment) => assignment.episode_id === episodeId && assignment.content.name === DA_CARE_CONSENT_CONTENT_NAME,
    )
    .flatMap((assignment) => assignment.assignment_responses ?? [])
    .find((response) => response.status === AssignmentStatuses.completed)

export const getCareOnboardingHpiPaymentReviewedEpisodeIds = (assignments: Assignment[]) =>
  new Set(
    assignments
      .filter(
        (assignment) =>
          assignment.content.name === DA_CARE_CONSENT_CONTENT_NAME &&
          assignment.assignment_responses?.some(
            (response) =>
              response.status === AssignmentStatuses.completed &&
              response.response?.[CARE_ONBOARDING_HPI_PAYMENT_REVIEWED_RESPONSE_KEY] === true,
          ),
      )
      .map((assignment) => assignment.episode_id),
  )

/**
 * Returns the first incomplete prerequisite when a client tries to enter a later Care Onboarding
 * assignment directly. Normal card navigation already does this; the guard closes deep-link and
 * stale-navigation gaps while preserving consent -> payment -> intake ordering.
 */
export const getBlockingCareOnboardingAssignment = ({
  selectedAssignment,
  linkedAssignments,
}: {
  selectedAssignment?: Assignment | null
  linkedAssignments: Assignment[]
}) => {
  if (!selectedAssignment) return undefined

  const selectedAssignmentIndex = linkedAssignments.findIndex((assignment) => assignment.id === selectedAssignment.id)
  const hasPaymentAssignment = linkedAssignments.some(isCareOnboardingHpiPaymentAssignment)
  const firstOpenAssignmentIndex = linkedAssignments.findIndex(
    (assignment) =>
      ![AssignmentStatuses.completed, AssignmentStatuses.missed].includes(assignment.status as AssignmentStatuses),
  )

  return hasPaymentAssignment && firstOpenAssignmentIndex >= 0 && selectedAssignmentIndex > firstOpenAssignmentIndex
    ? linkedAssignments[firstOpenAssignmentIndex]
    : undefined
}

const makeCareOnboardingHpiPaymentAssignment = (intakeAssignment: Assignment): Assignment => ({
  ...intakeAssignment,
  assignment_responses: [],
  content: {
    ...intakeAssignment.content,
    content_type: 'form',
    group: AssignmentGroup.ASSESSMENT,
    id: `${intakeAssignment.content.id ?? intakeAssignment.content_id}:hpi-payment`,
    name: DA_CARE_ONBOARDING_HPI_PAYMENT_CONTENT_NAME,
    title: 'Add payment information',
  },
  content_id: `${intakeAssignment.content_id}:hpi-payment`,
  content_meta_data: {},
  content_meta_data_id: `${intakeAssignment.content_meta_data_id}:hpi-payment`,
  draft_count: 0,
  id: `${intakeAssignment.id}:hpi-payment`,
  instructions: null,
  is_initial: false,
  past: false,
  progress: 0,
  response_count: 0,
  status: 'new',
  unread_count: 0,
})

/**
 * Adds a UI-only assignment to the linked Care Onboarding chain. It is never written to LyraTherapy;
 * it exists so the existing linked-assignment status, ordering, and resume behavior treat payment as
 * a required step until the episode's cost and payment information has been reviewed.
 */
export const withCareOnboardingHpiPaymentAssignments = ({
  assignments,
  context,
}: {
  assignments: Assignment[]
  context: CareOnboardingHpiPaymentContext
}) => {
  if (
    !context.hasFetchedPaymentMethods ||
    context.isZeroDollarCostShare ||
    assignments.some(isCareOnboardingHpiPaymentAssignment)
  ) {
    return assignments
  }

  const eligibleEpisodeIds = getCareOnboardingHpiPaymentEpisodeIds({
    assignments,
    episodes: context.episodes,
    programCoverage: context.programCoverage,
  })
  const intakeAssignmentsByEpisode = assignments.reduce((result, assignment) => {
    if (
      isDaCareOnboardingIntake(assignment) &&
      eligibleEpisodeIds.has(assignment.episode_id) &&
      context.estimateStateByEpisodeId[assignment.episode_id] !== 'loading' &&
      context.estimateStateByEpisodeId[assignment.episode_id] !== 'skip' &&
      !context.reviewedEpisodeIds.has(assignment.episode_id) &&
      !result.has(assignment.episode_id)
    ) {
      result.set(assignment.episode_id, assignment)
    }
    return result
  }, new Map<string, Assignment>())
  const paymentAssignments = [...intakeAssignmentsByEpisode.values()].map(makeCareOnboardingHpiPaymentAssignment)

  return paymentAssignments.length > 0 ? [...assignments, ...paymentAssignments] : assignments
}

/**
 * Keeps the virtual payment step in the current Care view even when every persisted onboarding
 * assignment has already moved out of the current list. Existing current assignments remain the
 * source of truth; only UI-only payment assignments are carried forward from the decorated list.
 */
export const withCurrentCareOnboardingHpiPaymentAssignments = ({
  currentAssignments,
  allAssignments,
}: {
  currentAssignments: Assignment[]
  allAssignments: Assignment[]
}) => {
  const currentIds = new Set(currentAssignments.map((assignment) => assignment.id))
  const missingPaymentAssignments = allAssignments.filter(
    (assignment) => isCareOnboardingHpiPaymentAssignment(assignment) && !currentIds.has(assignment.id),
  )

  return missingPaymentAssignments.length > 0
    ? [...currentAssignments, ...missingPaymentAssignments]
    : currentAssignments
}
